<script setup lang="ts">
import { Button } from '@/components/ui/button';
import EmptyState from '@/components/ui/empty-state/EmptyState.vue';
import RichTextContent from '@/components/editors/RichTextContent.vue';
import RichTextEditor from '@/components/editors/RichTextEditor.vue';
import SectionSurface from '@/components/workspace/SectionSurface.vue';
import WorkspaceDetailSheet from '@/components/workspace/WorkspaceDetailSheet.vue';
import { NotebookTabs, Pencil } from 'lucide-vue-next';

interface Props {
    modelValue: string;
    open: boolean;
    canMutate: boolean;
    saving: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void,
    (e: 'update:open', value: boolean): void,
    (e: 'save'): void,
}>();
</script>

<template>
    <SectionSurface title="Notes" description="Client context and handoff notes." :icon="NotebookTabs" density="compact" presentation="editorial">
        <template #actions>
            <Button v-if="canMutate" variant="outline" size="sm" @click="emit('update:open', true)"><Pencil class="mr-2 size-4" />Edit notes</Button>
        </template>
        <RichTextContent v-if="modelValue" :content="modelValue" class="line-clamp-6 text-sm text-foreground" />
        <EmptyState v-else embedded title="No client notes yet" description="Capture discovery context and handoff details in one place.">
            <template v-if="canMutate" #actions><Button variant="outline" size="sm" @click="emit('update:open', true)">Add notes</Button></template>
        </EmptyState>
    </SectionSurface>

    <WorkspaceDetailSheet :open="open" title="Client notes" description="Capture discovery context, commercial detail and team handoff notes." @update:open="emit('update:open', $event)">
        <RichTextEditor :model-value="modelValue" placeholder="Capture discovery detail, commercial context, and handoff notes here." @update:model-value="emit('update:modelValue', String($event || ''))" />
        <template #footer>
            <div class="flex justify-end gap-2"><Button variant="outline" @click="emit('update:open', false)">Cancel</Button><Button :disabled="saving" @click="emit('save')">{{ saving ? 'Saving…' : 'Save notes' }}</Button></div>
        </template>
    </WorkspaceDetailSheet>
</template>
